import React, { useState, useEffect, forwardRef, useMemo } from 'react';
import { Error } from '@progress/kendo-react-labels';
import BaseInput from '../BaseInput';
// Helpers
import { dateTimeFormatter } from '../../../constants/Formatting';

/**
 * @typedef {Omit<React.ComponentPropsWithoutRef<BaseInput>, 'value'>} BaseInputProps
 *
 * @typedef {Object} AdditionalCustomDateTimeInputProps
 * @property {string} [value] Represents the datetime string value.
 * @property {boolean} [visited] Indicates if the input is visited (focused and blurred).
 * @property {boolean} [autoFillYear={true}] Indicates if the value's year can be auto filled with the current year if another one is not set. True by default.
 *
 * @param {BaseInputProps & AdditionalCustomDateTimeInputProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const CustomDateTimeInput = ({
  id, onChange, value, onBlur, placeholder, visited, validationMessage, autoFillYear = true, timeZone, ...props
}, ref) => {
  const [textDate, setTextDate] = useState();

    const timeZoneName = useMemo(() => {

      if (!timeZone) {
        return null;
      }

      return new Intl.DateTimeFormat('en-us', { timeZoneName: 'short', timeZone })
        .formatToParts(new Date())
        .find(part => part.type == "timeZoneName")
        .value
    }, [timeZone])

    const setDisplayValue = (value) => {
      if (!value) {
        setTextDate(value);
        return
      }

      const newValue = timeZoneName ? `${value} ${timeZoneName}` : value;
      setTextDate(newValue);
    }
  const handleBlur = (e) => {
    if (e.target.value) {
      const formatted = dateTimeFormatter(e.target.value, autoFillYear);

      setDisplayValue(formatted);
      const newValue = new Date(formatted);

      // Check if any change have happened to avoid firing onChange for the same value
      if (newValue?.toISOString() !== value?.toISOString()) {
        onChange({ value: newValue });
      }
    } else {
      setDisplayValue('');

      // Check if Date value is not null already to avoid firing onChange for the same value
      if (value) {
        onChange({ value: null });
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  useEffect(() => {
    if (value) {
      setDisplayValue(dateTimeFormatter(value.toString()));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const showValidationMessage = visited && validationMessage;
  const errorId = showValidationMessage ? `${id}_error` : '';

  return (
    <>
      <BaseInput
        ref={ref}
        placeholder={placeholder || 'mm/dd/yyyy or days'}
        {...props}
        className='k-input-inner'
        onBlur={handleBlur}
        value={textDate}
        onChange={(e) => setTextDate(e.target.value)}
      />
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </>
  );
};

/**
 * A custom datetime input component.
 *
 * @param {BaseInputProps & AdditionalCustomDateTimeInputProps} props - Component props. Extends BaseInput props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Custom datetime input component.
 */
const ForwardCustomDateTimeInput = forwardRef(CustomDateTimeInput);

export default ForwardCustomDateTimeInput;
